import { TransactionList } from '@back-office/http';
import {
  GcpBox,
  GcpDateRangePicker,
  GcpMenuBar,
  GcpSearchBar,
  GcpTransactionListTable,
  GcpTypography,
  MenuBar,
  QrTypeMenu,
  TransactionStatus,
  TransactionSubStatus,
} from '@back-office/ui';
import { Button } from '@material-ui/core';
import Grid from '@material-ui/core/Grid';
import GetAppIcon from '@material-ui/icons/GetApp';
import { DateRange } from '@material-ui/pickers';
import { observer } from 'mobx-react';
import React, { ChangeEvent, FC, KeyboardEvent, MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { qrTypeItem, statusBar } from '../../constants';
import ExportToCsvDialog from '../transaction-list/ExportToCSVDialog';
import TransactionDetail from '../TransactionDetail';
import useViewModel from './viewmodel';

export interface Store {
  anchorEl: null | HTMLButtonElement;
  anchorElQrType: null | HTMLButtonElement;
  date: DateRange<Date>;
  handleMouseEvent: (isEnter: boolean) => void;
  handleOnChangeDatePicker: (date: DateRange<Date>) => void;
  handleOnChangePage: (newPage: number) => void;
  handleOnChangeSearchbar: (event: ChangeEvent<HTMLInputElement>) => void;
  handleOnClear: (event: MouseEvent<HTMLOrSVGElement>) => void;
  handleOnClickAway: () => void;
  handleOnClickBreadcrumbs: () => void;
  handleOnClickDatePicker: () => void;
  handleOnClickMenubar: (event: MouseEvent<HTMLButtonElement>) => void;
  handleOnClickMenuItem: (item: MenuBar | QrTypeMenu) => void;
  handleOnClickQrType: (event: MouseEvent<HTMLButtonElement>) => void;
  handleOnClickQrTypeItem: (item: QrTypeMenu | MenuBar) => void;
  handleOnClickRow: (transaction: TransactionList) => void;
  handleOnClickSearchbar: () => void;
  handleOnClose: () => void;
  handleOnCloseDatePicker: () => void;
  handleOnCloseQrType: () => void;
  handleOnGo: () => void;
  handleOnKeyDown: (event: KeyboardEvent<HTMLDivElement>) => void;
  isDetail: boolean;
  isMouseOver: boolean;
  keyword: string;
  openCalendar: boolean;
  openSearchBar: boolean;
  page: number;
  query: string;
  start: number;
  qrType: string | null;
  qrTypeLabe: string;
  status: TransactionStatus | null | '';
  subStatus: TransactionSubStatus | null | '';
  combineStatus: TransactionStatus | null | '';
  txId: string | null;
}

const TransactionLists: FC = observer(() => {
  const { t } = useTranslation();
  const { state, getDropdownPlaceholder, transaction, on, toggle } =
    useViewModel();

  return state.isDetail ? (
    <TransactionDetail
      handleOnClickBreadcrumbs={state.handleOnClickBreadcrumbs}
      query={state.query}
      txId={state.txId}
    />
  ) : (
    <GcpBox>
      <Grid container>
        <Grid container item alignItems="center" xs={3}>
          <GcpTypography variant="h3">
            {t('transaction_list.list')}
          </GcpTypography>
        </Grid>
        <Grid item xs={9}>
          <GcpBox display="flex" gap={2} justifyContent="flex-end">
            <GcpSearchBar
              handleOnChange={state.handleOnChangeSearchbar}
              handleOnClick={state.handleOnClickSearchbar}
              handleOnClickAway={state.handleOnClickAway}
              handleOnGo={state.handleOnGo}
              handleOnKeyDown={state.handleOnKeyDown}
              handleOnMouseEnter={() => state.handleMouseEvent(true)}
              handleOnMouseLeave={() => state.handleMouseEvent(false)}
              isMouseOver={state.isMouseOver}
              openSearchBar={state.openSearchBar}
              value={state.keyword}
            />
            <GcpDateRangePicker
              date={state.date}
              handleOnChange={state.handleOnChangeDatePicker}
              handleOnClear={state.handleOnClear}
              handleOnClick={state.handleOnClickDatePicker}
              handleOnClose={state.handleOnCloseDatePicker}
              openCalendar={state.openCalendar}
            />
            <GcpMenuBar<MenuBar>
              anchorEl={state.anchorEl}
              handleOnClick={state.handleOnClickMenubar}
              handleOnClickMenuItem={state.handleOnClickMenuItem}
              handleOnClose={state.handleOnClose}
              menuBar={statusBar}
              name={getDropdownPlaceholder(state.combineStatus)}
            />
            <GcpMenuBar<QrTypeMenu>
              anchorEl={state.anchorElQrType}
              handleOnClick={state.handleOnClickQrType}
              handleOnClickMenuItem={state.handleOnClickQrTypeItem}
              handleOnClose={state.handleOnCloseQrType}
              name={t(state.qrTypeLabe)}
              qrTypeMenu={qrTypeItem}
            />
            <Button
              color="primary"
              size="large"
              startIcon={<GetAppIcon />}
              type="button"
              variant="contained"
              onClick={() => toggle(true)}
            >
              {t('transaction_list.export')}
            </Button>
          </GcpBox>
        </Grid>
      </Grid>
      <GcpBox mt={3}>
        <GcpTransactionListTable
          data={transaction.list as TransactionList[]}
          handleOnChangePage={state.handleOnChangePage}
          handleOnClickRow={state.handleOnClickRow}
          page={state.page}
          pagination={transaction.pagination}
        />
      </GcpBox>
      <ExportToCsvDialog handleOnClose={() => toggle(false)} open={on} />
    </GcpBox>
  );
});

export default TransactionLists;
